import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, GripVertical, Search, Package, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import { useAuth } from '../../context/AuthContext';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable item component
const SortableSuggestion = ({ suggestion, onDelete, deleting }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: suggestion.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`bg-white rounded-xl p-4 shadow-sm border ${
        isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-5 w-5" />
        </button>
        
        <div className="flex-1">
          <h4 className="font-semibold">{suggestion.menu_item?.name}</h4>
          <p className="text-sm text-muted-foreground">{suggestion.menu_item?.category}</p>
          <p className="text-sm font-bold text-primary">£{suggestion.menu_item?.price?.toFixed(2)}</p>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(suggestion.id)}
          disabled={deleting === suggestion.id}
          data-testid="delete-suggestion-button"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
};

const AdminSuggestedProducts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [adding, setAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddPanel, setShowAddPanel] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [suggestionsRes, menuRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/suggested-products`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        menuAPI.getAllMenuItems()
      ]);
      setSuggestions(suggestionsRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuggestion = async (menuItemId) => {
    setAdding(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/suggested-products`,
        { menu_item_id: menuItemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions([...suggestions, response.data]);
      toast.success('Product added to suggestions');
      setShowAddPanel(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to add suggestion:', error);
      toast.error(error.response?.data?.detail || 'Failed to add suggestion');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteSuggestion = async (suggestionId) => {
    setDeleting(suggestionId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/suggested-products/${suggestionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(suggestions.filter(s => s.id !== suggestionId));
      toast.success('Product removed from suggestions');
    } catch (error) {
      console.error('Failed to delete suggestion:', error);
      toast.error('Failed to remove suggestion');
    } finally {
      setDeleting(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = suggestions.findIndex(s => s.id === active.id);
      const newIndex = suggestions.findIndex(s => s.id === over.id);
      
      const newOrder = arrayMove(suggestions, oldIndex, newIndex);
      setSuggestions(newOrder);
      
      // Update sort order in backend
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/api/admin/suggested-products/reorder`,
          { items: newOrder.map((s, i) => ({ id: s.id, sort_order: i })) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save order:', error);
        toast.error('Failed to save order');
        fetchData(); // Revert on error
      }
    }
  };

  // Filter menu items that are not already suggested
  const suggestedItemIds = suggestions.map(s => s.menu_item_id);
  const availableItems = menuItems.filter(item => 
    !suggestedItemIds.includes(item.id) &&
    item.available &&
    (searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Suggested Products" />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="bg-primary/20 rounded-full p-3">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-bold text-lg mb-1">Suggested Products</h2>
              <p className="text-muted-foreground text-sm">
                These products will be shown in the cart and checkout page to encourage additional purchases. 
                Maximum 5 products allowed. Drag to reorder.
              </p>
            </div>
          </div>
        </div>

        {/* Current Suggestions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Current Suggestions ({suggestions.length}/5)</h3>
            {suggestions.length < 5 && (
              <Button
                onClick={() => setShowAddPanel(!showAddPanel)}
                className="rounded-full"
                data-testid="add-suggestion-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No suggested products yet</p>
              <p className="text-sm text-muted-foreground mt-1">Add products to show in cart and checkout</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={suggestions.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  <AnimatePresence>
                    {suggestions.map(suggestion => (
                      <SortableSuggestion
                        key={suggestion.id}
                        suggestion={suggestion}
                        onDelete={handleDeleteSuggestion}
                        deleting={deleting}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Add Panel */}
        <AnimatePresence>
          {showAddPanel && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm border overflow-hidden"
            >
              <h3 className="font-bold text-lg mb-4">Add Product to Suggestions</h3>
              
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="search-products-input"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="max-h-80 overflow-y-auto space-y-2">
                {availableItems.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchQuery ? 'No matching products found' : 'All products are already suggested'}
                  </p>
                ) : (
                  availableItems.slice(0, 20).map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{item.category} • £{item.price?.toFixed(2)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => handleAddSuggestion(item.id)}
                        disabled={adding}
                        data-testid="add-product-button"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={() => setShowAddPanel(false)}>
                  Cancel
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminSuggestedProducts;
